import { useEffect, useState } from 'react';
import {
  Badge,
  Flex,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import EventService from 'services/event.api';
import { Event } from 'services/@types';
import { useNavigate } from 'react-router-dom';
import { buildEventParams } from 'views/admin/events/overview/buildEventParams';

interface Props {
  businessId: string;
}

const typeColor: Record<string, string> = {
  wedding: 'pink',
  birthday: 'purple',
  corporate: 'blue',
  conference: 'teal',
  party: 'orange',
};

export default function BusinessEventsTable({ businessId }: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    EventService.getEvents(
      buildEventParams({
        pageSize: 100,
        pageIndex: 1,
        sortBy: 'dateAndTime:desc',
      }),
    )
      .then((data) => {
        setEvents(data?.results || []);
      })
      .catch((err) => {
        console.error('Failed to fetch events:', err);
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [businessId]);

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const filtered = search
    ? events.filter((e) => {
        const name = (e.name || '').toLowerCase();
        const type = (e.type || '').toLowerCase();
        return (
          name.includes(search.toLowerCase()) ||
          type.includes(search.toLowerCase())
        );
      })
    : events;

  return (
    <Card p="30px">
      <Flex justify="space-between" align="center" mb="16px">
        <Text color={textColor} fontSize="xl" fontWeight="700">
          Events ({events.length})
        </Text>
        <Input
          placeholder="Search events..."
          size="sm"
          maxW="250px"
          borderRadius="8px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      {loading ? (
        <Flex justify="center" py="30px">
          <Spinner color="brand.500" />
        </Flex>
      ) : filtered.length === 0 ? (
        <Text color="gray.400" textAlign="center" py="20px">
          No events found
        </Text>
      ) : (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th color="gray.400">EVENT NAME</Th>
              <Th color="gray.400">TYPE</Th>
              <Th color="gray.400">DATE</Th>
              <Th color="gray.400">GUESTS</Th>
              <Th color="gray.400">STATUS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((event, i) => (
              <Tr
                key={event.id || i}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() =>
                  event.id && navigate(`/admin/events/detail/${event.id}`)
                }
              >
                <Td>
                  <Text color={textColor} fontSize="sm" fontWeight="500">
                    {event.name || '-'}
                  </Text>
                  {event.eventNumber != null && (
                    <Text color="gray.400" fontSize="xs">
                      #{event.eventNumber}
                    </Text>
                  )}
                </Td>
                <Td>
                  <Badge
                    colorScheme={typeColor[event.type || ''] || 'gray'}
                    fontSize="xs"
                  >
                    {event.type || '-'}
                  </Badge>
                </Td>
                <Td>
                  <Text color={textColor} fontSize="sm">
                    {formatDate(event.dateAndTime)}
                  </Text>
                </Td>
                <Td>
                  <Text color={textColor} fontSize="sm">
                    {event.numberOfGuests ?? '-'}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme={
                      event.dateAndTime &&
                      new Date(event.dateAndTime) > new Date()
                        ? 'green'
                        : 'gray'
                    }
                    fontSize="xs"
                  >
                    {event.dateAndTime &&
                    new Date(event.dateAndTime) > new Date()
                      ? 'Upcoming'
                      : 'Past'}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}
